import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Plus, ArrowLeft, Trash2, Save, Edit3, Clock, Ruler, User, ChevronRight,
  ChevronDown, ArrowRight, Diamond, Circle, Square, RotateCcw, Layers,
  GripVertical, X, FileDown, GitBranch, Workflow
} from 'lucide-react';

import { flowStepStyle } from '@/utils/styleHelpers';
import { PageTabs } from '@/components/PageTabs';

const HUB_TABS = [
  { path: '/vsm', label: 'Value Stream', icon: GitBranch },
  { path: '/process-flow', label: 'Process Flow', icon: Workflow },
];

const API = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_ICONS = { linear: ArrowRight, sipoc: Layers, swimlane: GripVertical, decision: Diamond, cycle: RotateCcw };
const TEMPLATE_COLORS = { linear: 'text-blue-400', sipoc: 'text-emerald-400', swimlane: 'text-amber-400', decision: 'text-rose-400', cycle: 'text-cyan-400' };
const TEMPLATE_BG = { linear: 'bg-blue-500/10 border-blue-500/20', sipoc: 'bg-emerald-500/10 border-emerald-500/20', swimlane: 'bg-amber-500/10 border-amber-500/20', decision: 'bg-rose-500/10 border-rose-500/20', cycle: 'bg-cyan-500/10 border-cyan-500/20' };
const TIME_UNITS = ['minutes', 'hours', 'days', 'weeks'];
const SCALES = ['individual', 'team', 'department', 'cross-functional', 'organization'];

// ========================== VISUAL RENDERERS ==========================

const LinearRenderer = ({ steps }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="linear-flow">
    {steps.map((s, i) => {
      const style = flowStepStyle(s);
      return (
        <div key={s.id} className="flex items-center gap-2">
          <div className={`px-4 py-3 rounded-lg border text-center min-w-[140px] ${style.bg}`}>
            <p className="text-sm font-medium text-foreground truncate">{s.label}</p>
            {s.assigned_to && <p className="text-xs text-muted-foreground mt-0.5"><User className="inline h-3 w-3 mr-1" />{s.assigned_to}</p>}
            {s.duration && <p className="text-xs text-muted-foreground"><Clock className="inline h-3 w-3 mr-1" />{s.duration}</p>}
          </div>
          {i < steps.length - 1 && <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />}
        </div>
      );
    })}
  </div>
);

const SIPOCRenderer = ({ steps }) => {
  const cols = ['supplier', 'input', 'process', 'output', 'customer'];
  const labels = ['Supplier', 'Input', 'Process', 'Output', 'Customer'];
  const colors = ['text-blue-400', 'text-cyan-400', 'text-amber-400', 'text-emerald-400', 'text-rose-400'];
  return (
    <div className="overflow-x-auto" data-testid="sipoc-flow">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>{labels.map((l, i) => <th key={l} className={`pb-3 px-3 text-center font-semibold ${colors[i]} border-b border-muted`}>{l}</th>)}</tr>
        </thead>
        <tbody>
          {steps.map(s => (
            <tr key={s.id} className="border-b border-muted/50">
              {cols.map(c => (
                <td key={c} className="px-3 py-3 text-center text-sm text-foreground/80 align-top">
                  <div>{s[c] || '-'}</div>
                  {c === 'process' && s.assigned_to && <div className="text-xs text-muted-foreground mt-1"><User className="inline h-3 w-3 mr-1" />{s.assigned_to}</div>}
                  {c === 'process' && s.duration && <div className="text-xs text-muted-foreground"><Clock className="inline h-3 w-3 mr-1" />{s.duration}</div>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SwimLaneRenderer = ({ steps, lanes = [] }) => {
  const phases = [...new Set(steps.map(s => s.phase).filter(Boolean))];
  return (
    <div className="overflow-x-auto" data-testid="swimlane-flow">
      <table className="w-full text-sm border-collapse min-w-[600px]">
        <thead>
          <tr>
            <th className="pb-3 px-3 text-left font-semibold text-muted-foreground border-b border-muted w-28">Lane</th>
            {phases.map(p => <th key={p} className="pb-3 px-3 text-center font-semibold text-amber-400 border-b border-muted">{p}</th>)}
          </tr>
        </thead>
        <tbody>
          {lanes.map(lane => (
            <tr key={lane} className="border-b border-muted/30">
              <td className="px-3 py-3 font-medium text-foreground/90 align-top border-r border-muted/30">{lane}</td>
              {phases.map(phase => {
                const cellSteps = steps.filter(s => s.lane === lane && s.phase === phase);
                return (
                  <td key={phase} className="px-2 py-2 align-top">
                    {cellSteps.map(s => (
                      <div key={s.id} className="mb-2 last:mb-0 p-2 rounded-md bg-amber-500/5 border border-amber-500/15">
                        <p className="text-xs font-medium text-foreground">{s.label}</p>
                        {s.assigned_to && <p className="text-xs text-muted-foreground mt-0.5"><User className="inline h-3 w-3 mr-0.5" />{s.assigned_to}</p>}
                        {s.duration && <p className="text-xs text-muted-foreground"><Clock className="inline h-3 w-3 mr-0.5" />{s.duration}</p>}
                      </div>
                    ))}
                    {cellSteps.length === 0 && <div className="text-xs text-muted-foreground/30 text-center">-</div>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const STEP_ICONS = {
  decision: (p) => <Diamond {...p} className="h-5 w-5 text-rose-400 fill-rose-400/20" />,
  start: (p) => <Circle {...p} className="h-5 w-5 text-blue-400 fill-blue-400/20" />,
  end: (p) => <Square {...p} className="h-5 w-5 text-emerald-400 fill-emerald-400/20" />,
  default: (p) => <ChevronDown {...p} className="h-5 w-5 text-muted-foreground" />,
};

const DecisionRenderer = ({ steps }) => (
  <div className="space-y-3" data-testid="decision-flow">
    {steps.map((s, i) => {
      const style = flowStepStyle(s);
      const indent = s.branch ? 'ml-8' : '';
      const StepIcon = STEP_ICONS[s.step_type] || STEP_ICONS.default;
      return (
        <div key={s.id} className={`flex items-start gap-3 ${indent}`}>
          <div className="flex flex-col items-center mt-1">
            <StepIcon />
          </div>
          <div className={`flex-1 p-3 rounded-lg border ${style.bg}`}>
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-foreground">{s.label}</p>
              {s.branch && <span className="text-xs px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20">{s.branch === 'yes' ? s.yes_label || 'Yes' : s.branch === 'no' || s.branch === 'no_s3' ? s.no_label || 'No' : s.branch}</span>}
            </div>
            {s.description && <p className="text-xs text-muted-foreground mt-1">{s.description}</p>}
            <div className="flex gap-3 mt-1">
              {s.assigned_to && <span className="text-xs text-muted-foreground"><User className="inline h-3 w-3 mr-0.5" />{s.assigned_to}</span>}
              {s.duration && <span className="text-xs text-muted-foreground"><Clock className="inline h-3 w-3 mr-0.5" />{s.duration}</span>}
            </div>
            {s.step_type === 'decision' && <div className="flex gap-2 mt-2"><span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{s.yes_label || 'Yes'}</span><span className="text-xs px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20">{s.no_label || 'No'}</span></div>}
          </div>
        </div>
      );
    })}
  </div>
);

const CycleRenderer = ({ steps }) => {
  const phases = ['Plan', 'Do', 'Check', 'Act'];
  const phaseColors = { Plan: 'border-blue-500/30 bg-blue-500/5', Do: 'border-emerald-500/30 bg-emerald-500/5', Check: 'border-amber-500/30 bg-amber-500/5', Act: 'border-cyan-500/30 bg-cyan-500/5' };
  const phaseTextColors = { Plan: 'text-blue-400', Do: 'text-emerald-400', Check: 'text-amber-400', Act: 'text-cyan-400' };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="cycle-flow">
      {phases.map((phase, pi) => {
        const phaseSteps = steps.filter(s => s.phase === phase);
        return (
          <div key={phase} className={`p-4 rounded-xl border ${phaseColors[phase]}`}>
            <div className="flex items-center gap-2 mb-3">
              <span className={`text-lg font-bold ${phaseTextColors[phase]}`}>{phase}</span>
              <RotateCcw className={`h-4 w-4 ${phaseTextColors[phase]}`} />
              {pi < 3 && <ArrowRight className={`h-4 w-4 ml-auto ${phaseTextColors[phase]}`} />}
              {pi === 3 && <RotateCcw className={`h-4 w-4 ml-auto ${phaseTextColors[phase]}`} />}
            </div>
            {phaseSteps.map(s => (
              <div key={s.id} className="mb-2 last:mb-0 p-2 rounded-md bg-background/30 border border-muted/30">
                <p className="text-sm font-medium text-foreground">{s.label}</p>
                {s.description && <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>}
                <div className="flex gap-3 mt-1">
                  {s.assigned_to && <span className="text-xs text-muted-foreground"><User className="inline h-3 w-3 mr-0.5" />{s.assigned_to}</span>}
                  {s.duration && <span className="text-xs text-muted-foreground"><Clock className="inline h-3 w-3 mr-0.5" />{s.duration}</span>}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

const FlowRenderer = ({ flow }) => {
  const t = flow.template_type;
  if (t === 'sipoc') return <SIPOCRenderer steps={flow.steps} />;
  if (t === 'swimlane') return <SwimLaneRenderer steps={flow.steps} lanes={flow.lanes} />;
  if (t === 'decision') return <DecisionRenderer steps={flow.steps} />;
  if (t === 'cycle') return <CycleRenderer steps={flow.steps} />;
  return <LinearRenderer steps={flow.steps} />;
};

// ========================== STEP EDITORS ==========================

const StepField = ({ label, value, onChange, placeholder, type = 'text', className = '' }) => (
  <div className={className}>
    <label className="text-xs text-muted-foreground mb-0.5 block">{label}</label>
    {type === 'textarea' ? (
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={2} className="w-full px-2 py-1.5 text-sm rounded-md bg-background border border-muted focus:border-primary/50 focus:ring-1 focus:ring-primary/30 resize-none" />
    ) : (
      <Input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="h-8 text-sm" />
    )}
  </div>
);

const LinearStepEditor = ({ step, onChange, onDelete, index, total }) => (
  <div className="p-4 rounded-lg border border-muted/50 bg-muted/10 space-y-2" data-testid={`step-editor-${step.id}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-muted-foreground">Step {index + 1} / {total}</span>
      <div className="flex gap-1">
        <select value={step.step_type || 'process'} onChange={e => onChange({ ...step, step_type: e.target.value })} className="text-xs px-2 py-1 rounded bg-background border border-muted">
          <option value="start">Start</option><option value="process">Process</option><option value="end">End</option>
        </select>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onDelete}><X className="h-3.5 w-3.5 text-destructive" /></Button>
      </div>
    </div>
    <StepField label="Label" value={step.label} onChange={v => onChange({ ...step, label: v })} placeholder="Step name" />
    <StepField label="Description" value={step.description} onChange={v => onChange({ ...step, description: v })} placeholder="What happens in this step?" type="textarea" />
    <div className="grid grid-cols-2 gap-2">
      <StepField label="Assigned to" value={step.assigned_to} onChange={v => onChange({ ...step, assigned_to: v })} placeholder="Person / role" />
      <StepField label="Duration" value={step.duration} onChange={v => onChange({ ...step, duration: v })} placeholder="e.g. 15 min" />
    </div>
  </div>
);

const SIPOCStepEditor = ({ step, onChange, onDelete, index }) => (
  <div className="p-4 rounded-lg border border-muted/50 bg-muted/10 space-y-2" data-testid={`step-editor-${step.id}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-muted-foreground">Row {index + 1}</span>
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onDelete}><X className="h-3.5 w-3.5 text-destructive" /></Button>
    </div>
    <div className="grid grid-cols-5 gap-2">
      {['supplier', 'input', 'process', 'output', 'customer'].map(f => (
        <StepField key={f} label={f.charAt(0).toUpperCase() + f.slice(1)} value={step[f]} onChange={v => onChange({ ...step, [f]: v })} placeholder={f} />
      ))}
    </div>
    <div className="grid grid-cols-2 gap-2">
      <StepField label="Assigned to" value={step.assigned_to} onChange={v => onChange({ ...step, assigned_to: v })} placeholder="Person / role" />
      <StepField label="Duration" value={step.duration} onChange={v => onChange({ ...step, duration: v })} placeholder="e.g. 2 hours" />
    </div>
  </div>
);

const SwimLaneStepEditor = ({ step, onChange, onDelete, index, lanes }) => (
  <div className="p-4 rounded-lg border border-muted/50 bg-muted/10 space-y-2" data-testid={`step-editor-${step.id}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-muted-foreground">Step {index + 1}</span>
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onDelete}><X className="h-3.5 w-3.5 text-destructive" /></Button>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="text-xs text-muted-foreground mb-0.5 block">Lane</label>
        <select value={step.lane || ''} onChange={e => onChange({ ...step, lane: e.target.value })} className="w-full h-8 text-sm px-2 rounded-md bg-background border border-muted">
          {lanes.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>
      <StepField label="Phase" value={step.phase} onChange={v => onChange({ ...step, phase: v })} placeholder="e.g. Initiation" />
    </div>
    <StepField label="Label" value={step.label} onChange={v => onChange({ ...step, label: v })} placeholder="Step name" />
    <StepField label="Description" value={step.description} onChange={v => onChange({ ...step, description: v })} placeholder="Description" type="textarea" />
    <div className="grid grid-cols-2 gap-2">
      <StepField label="Assigned to" value={step.assigned_to} onChange={v => onChange({ ...step, assigned_to: v })} placeholder="Person / role" />
      <StepField label="Duration" value={step.duration} onChange={v => onChange({ ...step, duration: v })} placeholder="e.g. 1 hour" />
    </div>
  </div>
);

const DecisionStepEditor = ({ step, onChange, onDelete, index }) => (
  <div className="p-4 rounded-lg border border-muted/50 bg-muted/10 space-y-2" data-testid={`step-editor-${step.id}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-muted-foreground">Step {index + 1}</span>
      <div className="flex gap-1">
        <select value={step.step_type || 'process'} onChange={e => onChange({ ...step, step_type: e.target.value })} className="text-xs px-2 py-1 rounded bg-background border border-muted">
          <option value="start">Start</option><option value="process">Process</option><option value="decision">Decision</option><option value="end">End</option>
        </select>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onDelete}><X className="h-3.5 w-3.5 text-destructive" /></Button>
      </div>
    </div>
    <StepField label="Label" value={step.label} onChange={v => onChange({ ...step, label: v })} placeholder="Step or decision name" />
    <StepField label="Description" value={step.description} onChange={v => onChange({ ...step, description: v })} placeholder="Description" type="textarea" />
    {step.step_type === 'decision' && (
      <div className="grid grid-cols-2 gap-2">
        <StepField label="Yes → label" value={step.yes_label} onChange={v => onChange({ ...step, yes_label: v })} placeholder="Yes path" />
        <StepField label="No → label" value={step.no_label} onChange={v => onChange({ ...step, no_label: v })} placeholder="No path" />
      </div>
    )}
    <div className="grid grid-cols-3 gap-2">
      <StepField label="Branch" value={step.branch} onChange={v => onChange({ ...step, branch: v })} placeholder="yes / no" />
      <StepField label="Assigned to" value={step.assigned_to} onChange={v => onChange({ ...step, assigned_to: v })} placeholder="Person / role" />
      <StepField label="Duration" value={step.duration} onChange={v => onChange({ ...step, duration: v })} placeholder="e.g. 5 min" />
    </div>
  </div>
);

const CycleStepEditor = ({ step, onChange, onDelete, index }) => (
  <div className="p-4 rounded-lg border border-muted/50 bg-muted/10 space-y-2" data-testid={`step-editor-${step.id}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-muted-foreground">Step {index + 1}</span>
      <div className="flex gap-1">
        <select value={step.phase || 'Plan'} onChange={e => onChange({ ...step, phase: e.target.value })} className="text-xs px-2 py-1 rounded bg-background border border-muted">
          <option value="Plan">Plan</option><option value="Do">Do</option><option value="Check">Check</option><option value="Act">Act</option>
        </select>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onDelete}><X className="h-3.5 w-3.5 text-destructive" /></Button>
      </div>
    </div>
    <StepField label="Label" value={step.label} onChange={v => onChange({ ...step, label: v })} placeholder="Step name" />
    <StepField label="Description" value={step.description} onChange={v => onChange({ ...step, description: v })} placeholder="Description" type="textarea" />
    <div className="grid grid-cols-2 gap-2">
      <StepField label="Assigned to" value={step.assigned_to} onChange={v => onChange({ ...step, assigned_to: v })} placeholder="Person / role" />
      <StepField label="Duration" value={step.duration} onChange={v => onChange({ ...step, duration: v })} placeholder="e.g. 3 days" />
    </div>
  </div>
);

const StepEditor = ({ step, onChange, onDelete, index, total, templateType, lanes }) => {
  if (templateType === 'sipoc') return <SIPOCStepEditor step={step} onChange={onChange} onDelete={onDelete} index={index} />;
  if (templateType === 'swimlane') return <SwimLaneStepEditor step={step} onChange={onChange} onDelete={onDelete} index={index} lanes={lanes || []} />;
  if (templateType === 'decision') return <DecisionStepEditor step={step} onChange={onChange} onDelete={onDelete} index={index} />;
  if (templateType === 'cycle') return <CycleStepEditor step={step} onChange={onChange} onDelete={onDelete} index={index} />;
  return <LinearStepEditor step={step} onChange={onChange} onDelete={onDelete} index={index} total={total} />;
};

// ========================== DETAIL / EDIT VIEW ==========================

const FlowDetail = ({ flowId, onBack, token, isAdmin }) => {
  const [flow, setFlow] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/process-flows/${flowId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Failed to load');
      const d = await r.json();
      setFlow(d);
    } catch { toast.error('Failed to load process flow'); }
  }, [flowId, token]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => { setDraft(JSON.parse(JSON.stringify(flow))); setEditing(true); };
  const cancelEdit = () => { setDraft(null); setEditing(false); };

  const exportPdf = async () => {
    try {
      const r = await fetch(`${API}/api/process-flows/export/${flowId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Export failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = r.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="(.+?)"/);
      a.download = match ? match[1] : 'process_flow.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF exported');
    } catch { toast.error('PDF export failed'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/process-flows/${flowId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: draft.name, description: draft.description, steps: draft.steps, settings: draft.settings, lanes: draft.lanes }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Save failed'); }
      const updated = await r.json();
      setFlow(updated); setDraft(null); setEditing(false);
      toast.success('Process flow saved');
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const updateStep = (idx, updated) => { const s = [...draft.steps]; s[idx] = updated; setDraft({ ...draft, steps: s }); };
  const deleteStep = (idx) => { setDraft({ ...draft, steps: draft.steps.filter((_, i) => i !== idx) }); };
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const handleDragStart = (i) => setDragIdx(i);
  const handleDragOver = (e, i) => { e.preventDefault(); setDragOver(i); };
  const handleDrop = (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setDragOver(null); return; }
    const steps = [...draft.steps];
    const [moved] = steps.splice(dragIdx, 1);
    steps.splice(i, 0, moved);
    setDraft({ ...draft, steps });
    setDragIdx(null);
    setDragOver(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setDragOver(null); };
  const addStep = () => {
    const id = `s${Date.now()}`;
    const t = draft.template_type;
    let newStep;
    if (t === 'sipoc') newStep = { id, supplier: '', input: '', process: '', output: '', customer: '', assigned_to: '', duration: '' };
    else if (t === 'swimlane') newStep = { id, lane: draft.lanes?.[0] || '', label: '', description: '', assigned_to: '', duration: '', phase: '' };
    else if (t === 'decision') newStep = { id, label: '', description: '', assigned_to: '', duration: '', step_type: 'process', branch: '' };
    else if (t === 'cycle') newStep = { id, label: '', description: '', assigned_to: '', duration: '', phase: 'Plan' };
    else newStep = { id, label: '', description: '', assigned_to: '', duration: '', step_type: 'process' };
    setDraft({ ...draft, steps: [...draft.steps, newStep] });
  };

  const addLane = () => {
    if (draft.template_type !== 'swimlane') return;
    const name = prompt('Enter lane name (e.g. department or role):');
    if (name?.trim()) setDraft({ ...draft, lanes: [...(draft.lanes || []), name.trim()] });
  };
  const removeLane = (idx) => { setDraft({ ...draft, lanes: draft.lanes.filter((_, i) => i !== idx) }); };

  if (!flow) return <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const d = editing ? draft : flow;
  const Icon = TEMPLATE_ICONS[d.template_type] || ArrowRight;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="pf-back"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          {editing ? <Input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} className="text-lg font-semibold h-10" data-testid="pf-name-input" />
            : <h2 className="text-xl font-semibold text-foreground" data-testid="pf-name">{d.name}</h2>}
          <div className="flex items-center gap-3 mt-1">
            <span className={`inline-flex items-center gap-1 text-xs ${TEMPLATE_COLORS[d.template_type]}`}><Icon className="h-3.5 w-3.5" />{d.template_type}</span>
            <span className="text-xs text-muted-foreground">by {d.created_by_name}</span>
            <span className="text-xs text-muted-foreground">updated {new Date(d.updated_at).toLocaleDateString()}</span>
          </div>
        </div>
        {isAdmin && !editing && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportPdf} data-testid="pf-export-pdf"><FileDown className="h-4 w-4 mr-1.5" />PDF</Button>
            <Button onClick={startEdit} data-testid="pf-edit-btn"><Edit3 className="h-4 w-4 mr-2" />Edit</Button>
          </div>
        )}
        {!isAdmin && !editing && (
          <Button variant="outline" size="sm" onClick={exportPdf} data-testid="pf-export-pdf"><FileDown className="h-4 w-4 mr-1.5" />PDF</Button>
        )}
        {editing && <>
          <Button variant="outline" onClick={cancelEdit}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="pf-save-btn"><Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}</Button>
        </>}
      </div>

      {editing ? (
        <div className="space-y-4">
          <StepField label="Description" value={draft.description} onChange={v => setDraft({ ...draft, description: v })} placeholder="Process description" type="textarea" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground mb-0.5 block">Time Unit</label>
              <select value={draft.settings?.time_unit || 'minutes'} onChange={e => setDraft({ ...draft, settings: { ...draft.settings, time_unit: e.target.value } })} className="w-full h-8 text-sm px-2 rounded-md bg-background border border-muted" data-testid="pf-time-unit">
                {TIME_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-0.5 block">Scale</label>
              <select value={draft.settings?.scale || 'department'} onChange={e => setDraft({ ...draft, settings: { ...draft.settings, scale: e.target.value } })} className="w-full h-8 text-sm px-2 rounded-md bg-background border border-muted" data-testid="pf-scale">
                {SCALES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {draft.template_type === 'swimlane' && (
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Lanes</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {(draft.lanes || []).map((l, i) => (
                  <span key={`lane-${l}-${i}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-400">
                    {l} <button onClick={() => removeLane(i)} className="hover:text-destructive"><X className="h-3 w-3" /></button>
                  </span>
                ))}
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addLane} data-testid="pf-add-lane"><Plus className="h-3 w-3 mr-1" />Add Lane</Button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Steps ({draft.steps.length})</h3>
              <Button variant="outline" size="sm" onClick={addStep} data-testid="pf-add-step"><Plus className="h-4 w-4 mr-1" />Add Step</Button>
            </div>
            {draft.steps.map((s, i) => (
              <div
                key={s.id}
                draggable
                onDragStart={() => handleDragStart(i)}
                onDragOver={(e) => handleDragOver(e, i)}
                onDrop={() => handleDrop(i)}
                onDragEnd={handleDragEnd}
                className={`relative transition-all ${dragOver === i ? 'ring-2 ring-primary/50 ring-offset-1 rounded-lg' : ''} ${dragIdx === i ? 'opacity-40' : ''}`}
                data-testid={`pf-step-drag-${i}`}
              >
                <div className="absolute left-0 top-3 -ml-6 cursor-grab active:cursor-grabbing" data-testid={`pf-drag-handle-${i}`}>
                  <GripVertical className="h-4 w-4 text-muted-foreground/50 hover:text-muted-foreground" />
                </div>
                <StepEditor key={s.id} step={s} onChange={u => updateStep(i, u)} onDelete={() => deleteStep(i)} index={i} total={draft.steps.length} templateType={draft.template_type} lanes={draft.lanes} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {d.description && <p className="text-sm text-muted-foreground">{d.description}</p>}
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground px-2 py-1 rounded-md bg-muted/30 border border-muted/50"><Clock className="h-3.5 w-3.5" />Time unit: {d.settings?.time_unit || 'minutes'}</span>
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground px-2 py-1 rounded-md bg-muted/30 border border-muted/50"><Ruler className="h-3.5 w-3.5" />Scale: {d.settings?.scale || 'department'}</span>
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground px-2 py-1 rounded-md bg-muted/30 border border-muted/50"><Layers className="h-3.5 w-3.5" />{d.steps?.length || 0} steps</span>
          </div>
          <div className="p-4 rounded-xl border border-muted/50 bg-muted/5">
            <FlowRenderer flow={d} />
          </div>
        </div>
      )}
    </div>
  );
};

// ========================== LIST VIEW ==========================

const FlowList = ({ onSelect, token, isAdmin }) => {
  const [flows, setFlows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  const load = useCallback(async () => {
    try {
      const [fr, tr] = await Promise.all([
        fetch(`${API}/api/process-flows`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/process-flows/templates`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (fr.ok) setFlows(await fr.json());
      if (tr.ok) setTemplates(await tr.json());
    } catch { toast.error('Failed to load process flows'); }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const createFlow = async (templateType) => {
    setCreating(true);
    try {
      const r = await fetch(`${API}/api/process-flows`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ template_type: templateType }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Create failed'); }
      const flow = await r.json();
      toast.success('Process flow created');
      onSelect(flow.flow_id);
    } catch (e) { toast.error(e.message); } finally { setCreating(false); }
  };

  const deleteFlow = async (flowId, e) => {
    e.stopPropagation();
    if (!confirm('Delete this process flow?')) return;
    try {
      const r = await fetch(`${API}/api/process-flows/${flowId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Delete failed');
      toast.success('Process flow deleted');
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Process Flows</h2>
          <p className="text-sm text-muted-foreground mt-1">Create and manage process maps for your operations</p>
        </div>
        {isAdmin && <Button onClick={() => setShowTemplates(!showTemplates)} disabled={creating} data-testid="pf-new-btn"><Plus className="h-4 w-4 mr-2" />New Process Flow</Button>}
      </div>

      {showTemplates && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 rounded-xl border border-primary/20 bg-primary/5" data-testid="pf-template-picker">
          <p className="col-span-full text-sm font-medium text-foreground mb-1">Choose a template:</p>
          {templates.map(t => {
            const Icon = TEMPLATE_ICONS[t.type] || ArrowRight;
            return (
              <button key={t.type} onClick={() => { createFlow(t.type); setShowTemplates(false); }}
                className={`p-4 rounded-lg border text-left transition-all hover:shadow-md cursor-pointer ${TEMPLATE_BG[t.type]}`}
                data-testid={`pf-template-${t.type}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`h-5 w-5 ${TEMPLATE_COLORS[t.type]}`} />
                  <span className="text-sm font-semibold text-foreground">{t.name}</span>
                </div>
                <p className="text-xs text-muted-foreground">{t.description}</p>
              </button>
            );
          })}
        </div>
      )}

      {flows.length === 0 && !showTemplates ? (
        <div className="text-center py-16 text-muted-foreground">
          <Layers className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg">No process flows yet</p>
          <p className="text-sm mt-1">Create your first process flow from a template</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {flows.map(f => {
            const Icon = TEMPLATE_ICONS[f.template_type] || ArrowRight;
            return (
              <div key={f.flow_id} onClick={() => onSelect(f.flow_id)}
                className="flex items-center gap-4 p-4 rounded-xl border border-muted/50 bg-card hover:border-primary/30 hover:bg-card/80 transition-all cursor-pointer group"
                data-testid={`pf-item-${f.flow_id}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${TEMPLATE_BG[f.template_type]}`}>
                  <Icon className={`h-5 w-5 ${TEMPLATE_COLORS[f.template_type]}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{f.name}</p>
                  <p className="text-xs text-muted-foreground">{f.steps?.length || 0} steps &middot; {f.template_type} &middot; {f.settings?.scale || 'department'}</p>
                </div>
                <span className="text-xs text-muted-foreground hidden sm:block">{new Date(f.updated_at).toLocaleDateString()}</span>
                {isAdmin && (
                  <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => deleteFlow(f.flow_id, e)} data-testid={`pf-delete-${f.flow_id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ========================== MAIN PAGE ==========================

export const ProcessFlowPage = () => {
  const { user } = useAuth();
  const [selectedFlow, setSelectedFlow] = useState(null);
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const isAdmin = user?.role === 'admin' || user?.role === 'master_admin' || user?.is_master_admin;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="process-flow-page">
        {!selectedFlow && <PageTabs tabs={HUB_TABS} testIdPrefix="pf-tab" />}
        {selectedFlow ? (
          <FlowDetail flowId={selectedFlow} onBack={() => setSelectedFlow(null)} token={token} isAdmin={isAdmin} />
        ) : (
          <FlowList onSelect={setSelectedFlow} token={token} isAdmin={isAdmin} />
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProcessFlowPage;
